import { useState } from "react";
import { Bell } from "lucide-react";
import { SlackIcon } from "@/components/icons/SlackIcon";
import { SectionCard } from "./SectionCard";
import { ConfigureSlackModal } from "./ConfigureSlackModal";

interface NotificationSetting {
  id: number;
  platform: string;
  destination: string;
  metadata: {
    channel_name: string;
  };
}

interface NotificationSettingsSectionProps {
  workspaceSlug: string;
  awsAccountUuid?: string;
  githubRepositoryUuid?: string;
  notificationSettings: NotificationSetting[];
  integrations: {
    slack_connected: boolean;
    slack_workspace_url?: string;
  };
  canEdit: boolean;
}

export function NotificationSettingsSection({
  workspaceSlug,
  awsAccountUuid,
  githubRepositoryUuid,
  notificationSettings,
  integrations,
  canEdit,
}: NotificationSettingsSectionProps) {
  const [isSlackModalOpen, setIsSlackModalOpen] = useState(false);

  const slackSetting = notificationSettings.find(
    (setting) => setting.platform === "slack"
  );

  return (
    <>
      <SectionCard title="Notifications" icon={Bell} className="mt-6">
        <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
          {/* Slack */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2.5">
              <SlackIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Slack
                </h3>
                {slackSetting ? (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Sending to{" "}
                    {integrations.slack_workspace_url ? (
                      <a
                        href={new URL(
                          `/archives/${slackSetting.destination}`,
                          integrations.slack_workspace_url
                        ).toString()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:underline"
                      >
                        {slackSetting.metadata.channel_name}
                      </a>
                    ) : (
                      <span className="font-medium">
                        {slackSetting.metadata.channel_name}
                      </span>
                    )}
                  </p>
                ) : integrations.slack_connected ? (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    No channel configured
                  </p>
                ) : (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Connect Slack in workspace settings first
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {integrations.slack_connected ? (
                canEdit ? (
                  <button
                    onClick={() => setIsSlackModalOpen(true)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
                  >
                    Configure
                  </button>
                ) : (
                  <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                    No permission
                  </span>
                )
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                  Connect Slack first
                </span>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      {integrations.slack_connected && (
        <ConfigureSlackModal
          isOpen={isSlackModalOpen}
          onClose={() => setIsSlackModalOpen(false)}
          workspaceSlug={workspaceSlug}
          awsAccountUuid={awsAccountUuid}
          githubRepositoryUuid={githubRepositoryUuid}
          existingSetting={slackSetting}
        />
      )}
    </>
  );
}
